import { Rule } from 'antd/lib/form';
import FormSelect from 'components/Select/FormSelect';
import { ISelectOption } from 'components/Select/types';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setRestaurants: (value: string[]) => void;
	selectedCustomer: Nullable<string>;
	selectedRestaurants: string[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	selectedRestaurants,
	selectedCustomer,
	setRestaurants,
}) => {
	const [searchString, setSearchString] = useState<string>('');
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);
	const { t } = useTranslation('error');

	const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectOneRestaurant') }], [t]);

	const visibleData = useMemo(() => {
		const items = showArchived
			? data
			: data.filter((el) => el.status === EntityStatus.Active || selectedRestaurants.includes(el.id));

		return items.map((el) => ({
			...el,
			additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [data, showArchived, selectedRestaurants]);

	const filteredData = useMemo<ISelectOption[]>(() => {
		return searchString
			? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: visibleData;
	}, [searchString, visibleData]);

	const handleSelectAllData = useCallback(() => {
		if (visibleData.length === selectedRestaurants.length) {
			setRestaurants([]);
		} else {
			setRestaurants(visibleData.map((el) => el.id));
		}
	}, [visibleData, selectedRestaurants, setRestaurants]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	useEffect(() => {
		setSearchString('');
	}, [selectedCustomer]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={!!(visibleData.length && visibleData.length === selectedRestaurants.length)}
							disabled={!visibleData.length}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleData,
			selectedRestaurants,
			handleSelectAllData,
			searchString,
			setSearchString,
			showArchived,
			toggleShowArchived,
		],
	);

	return (
		<FormSelect
			name="selectedRestaurants"
			label="restaurantsLabel"
			options={filteredData}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			onChange={setRestaurants}
			disabled={!selectedCustomer || isLoading}
			validationRules={validationRules}
			dropdownRender={renderSelectMenu}
			mode="multiple"
			counterValue={selectedRestaurants.length}
		/>
	);
};

export default React.memo(MultipleSelect);
